import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { TimerFormDraft } from './timerPageSlice';

const emptyDraft: TimerFormDraft = {
  projectId: null,
  taskId: null,
  tagIds: [],
  description: '',
  billableOverride: null,
  date: null,
  startTime: null,
  endTime: null,
  duration: null,
};

export type DragState = 'none' | 'creating' | 'moving' | 'resizing-start' | 'resizing-end';

export type DragGhost = {
  date: string;
  startTime: string;
  endTime: string;
};

export type ViewMode = 'day' | 'week';

export type CalendarPageState = {
  viewMode: ViewMode;
  viewedDate: string;
  dragState: DragState;
  dragEntryId: string | null;
  dragGhost: DragGhost | null;
  pendingResizeToZeroConfirm: { entryId: string } | null;
  pendingOver12hWarn: boolean;
  selectedEntryId: string | null;
  editPanelOpen: boolean;
  editPanelDraft: TimerFormDraft;
  createModalOpen: boolean;
  createModalDraft: TimerFormDraft;
  submitInProgress: boolean;
  lastErrorKey: string | null;
};

const initialState: CalendarPageState = {
  viewMode: 'week',
  viewedDate: '',
  dragState: 'none',
  dragEntryId: null,
  dragGhost: null,
  pendingResizeToZeroConfirm: null,
  pendingOver12hWarn: false,
  selectedEntryId: null,
  editPanelOpen: false,
  editPanelDraft: emptyDraft,
  createModalOpen: false,
  createModalDraft: emptyDraft,
  submitInProgress: false,
  lastErrorKey: null,
};

export const calendarPageSlice = createSlice({
  name: 'calendarPage',
  initialState,
  reducers: {
    setCalendarViewMode(state, action: PayloadAction<ViewMode>) {
      state.viewMode = action.payload;
    },
    setCalendarViewedDate(state, action: PayloadAction<string>) {
      state.viewedDate = action.payload;
    },
    setCalendarDragState(state, action: PayloadAction<DragState>) {
      state.dragState = action.payload;
    },
    setCalendarDragEntryId(state, action: PayloadAction<string | null>) {
      state.dragEntryId = action.payload;
    },
    setCalendarDragGhost(state, action: PayloadAction<DragGhost | null>) {
      state.dragGhost = action.payload;
    },
    setCalendarPendingResizeToZeroConfirm(
      state,
      action: PayloadAction<{ entryId: string } | null>,
    ) {
      state.pendingResizeToZeroConfirm = action.payload;
    },
    setCalendarPendingOver12hWarn(state, action: PayloadAction<boolean>) {
      state.pendingOver12hWarn = action.payload;
    },
    setCalendarSelectedEntryId(state, action: PayloadAction<string | null>) {
      state.selectedEntryId = action.payload;
    },
    setCalendarEditPanelOpen(state, action: PayloadAction<boolean>) {
      state.editPanelOpen = action.payload;
    },
    setCalendarEditPanelDraftProjectId(state, action: PayloadAction<string | null>) {
      state.editPanelDraft.projectId = action.payload;
      state.editPanelDraft.taskId = null;
      state.editPanelDraft.tagIds = [];
      state.editPanelDraft.billableOverride = null;
    },
    setCalendarEditPanelDraftTaskId(state, action: PayloadAction<string | null>) {
      state.editPanelDraft.taskId = action.payload;
    },
    setCalendarEditPanelDraftTagIds(state, action: PayloadAction<string[]>) {
      state.editPanelDraft.tagIds = action.payload;
    },
    setCalendarEditPanelDraftDescription(state, action: PayloadAction<string>) {
      state.editPanelDraft.description = action.payload;
    },
    setCalendarEditPanelDraftBillableOverride(state, action: PayloadAction<boolean | null>) {
      state.editPanelDraft.billableOverride = action.payload;
    },
    setCalendarEditPanelDraftDate(state, action: PayloadAction<string | null>) {
      state.editPanelDraft.date = action.payload;
    },
    setCalendarEditPanelDraftStartTime(state, action: PayloadAction<string | null>) {
      state.editPanelDraft.startTime = action.payload;
    },
    setCalendarEditPanelDraftEndTime(state, action: PayloadAction<string | null>) {
      state.editPanelDraft.endTime = action.payload;
    },
    setCalendarSubmitInProgress(state, action: PayloadAction<boolean>) {
      state.submitInProgress = action.payload;
    },
    setCalendarLastErrorKey(state, action: PayloadAction<string | null>) {
      state.lastErrorKey = action.payload;
    },
    prefillCalendarEditPanel(
      state,
      action: PayloadAction<{
        entryId: string;
        projectId: string;
        taskId: string | null;
        tagIds: string[];
        description: string;
        billable: boolean;
        date: string;
        startTime: string;
        endTime: string | null;
      }>,
    ) {
      const { entryId, projectId, taskId, tagIds, description, billable, date, startTime, endTime } =
        action.payload;
      state.selectedEntryId = entryId;
      state.editPanelOpen = true;
      state.editPanelDraft.projectId = projectId;
      state.editPanelDraft.taskId = taskId;
      state.editPanelDraft.tagIds = tagIds;
      state.editPanelDraft.description = description;
      state.editPanelDraft.billableOverride = billable;
      state.editPanelDraft.date = date;
      state.editPanelDraft.startTime = startTime;
      state.editPanelDraft.endTime = endTime;
      state.lastErrorKey = null;
    },
    resetCalendarEditPanel(state) {
      state.editPanelOpen = false;
      state.selectedEntryId = null;
      state.editPanelDraft = emptyDraft;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    openCalendarCreateModal(
      state,
      action: PayloadAction<{ date: string; startTime: string; endTime: string }>,
    ) {
      const { date, startTime, endTime } = action.payload;
      state.createModalOpen = true;
      state.createModalDraft = { ...emptyDraft, date, startTime, endTime };
      state.lastErrorKey = null;
    },
    setCalendarCreateModalDraftProjectId(state, action: PayloadAction<string | null>) {
      state.createModalDraft.projectId = action.payload;
      state.createModalDraft.taskId = null;
      state.createModalDraft.tagIds = [];
      state.createModalDraft.billableOverride = null;
    },
    setCalendarCreateModalDraftTaskId(state, action: PayloadAction<string | null>) {
      state.createModalDraft.taskId = action.payload;
    },
    setCalendarCreateModalDraftTagIds(state, action: PayloadAction<string[]>) {
      state.createModalDraft.tagIds = action.payload;
    },
    setCalendarCreateModalDraftDescription(state, action: PayloadAction<string>) {
      state.createModalDraft.description = action.payload;
    },
    setCalendarCreateModalDraftBillableOverride(state, action: PayloadAction<boolean | null>) {
      state.createModalDraft.billableOverride = action.payload;
    },
    setCalendarCreateModalDraftStartTime(state, action: PayloadAction<string | null>) {
      state.createModalDraft.startTime = action.payload;
    },
    setCalendarCreateModalDraftEndTime(state, action: PayloadAction<string | null>) {
      state.createModalDraft.endTime = action.payload;
    },
    setCalendarCreateModalDraftDuration(state, action: PayloadAction<string | null>) {
      state.createModalDraft.duration = action.payload;
    },
    resetCalendarCreateModal(state) {
      state.createModalOpen = false;
      state.createModalDraft = emptyDraft;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    resetCalendarPage() {
      return initialState;
    },
  },
});

export const {
  setCalendarViewMode,
  setCalendarViewedDate,
  setCalendarDragState,
  setCalendarDragEntryId,
  setCalendarDragGhost,
  setCalendarPendingResizeToZeroConfirm,
  setCalendarPendingOver12hWarn,
  setCalendarSelectedEntryId,
  setCalendarEditPanelOpen,
  setCalendarEditPanelDraftProjectId,
  setCalendarEditPanelDraftTaskId,
  setCalendarEditPanelDraftTagIds,
  setCalendarEditPanelDraftDescription,
  setCalendarEditPanelDraftBillableOverride,
  setCalendarEditPanelDraftDate,
  setCalendarEditPanelDraftStartTime,
  setCalendarEditPanelDraftEndTime,
  setCalendarSubmitInProgress,
  setCalendarLastErrorKey,
  prefillCalendarEditPanel,
  resetCalendarEditPanel,
  openCalendarCreateModal,
  setCalendarCreateModalDraftProjectId,
  setCalendarCreateModalDraftTaskId,
  setCalendarCreateModalDraftTagIds,
  setCalendarCreateModalDraftDescription,
  setCalendarCreateModalDraftBillableOverride,
  setCalendarCreateModalDraftStartTime,
  setCalendarCreateModalDraftEndTime,
  setCalendarCreateModalDraftDuration,
  resetCalendarCreateModal,
  resetCalendarPage,
} = calendarPageSlice.actions;

type AppRootState = { calendarPage: CalendarPageState };
export const selectCalendarPage = (state: AppRootState) => state.calendarPage;
